import { ReactElement, SyntheticEvent, use, useEffect, useState } from 'react';
import { alpha, Checkbox } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import Constants from 'style/constants';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import OrganisationUnitTreeItem from 'shared/features/organisation/components/organisation-tree-item/organisation-unit-tree-item';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

interface IProps {
  onSelectOrganisationUnit: (selectedNode: OrganisationTreeNode) => void;
  selectedOrganisationUnitIds: string[];
  disabledChildrenOfOrganisationUnitIds: string[];
}

export default function MultiSelectAlternativeDiversionTree(props: Readonly<IProps>) {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const nodesByNodeId = new Map<string, OrganisationTreeNode>();

  const { organisationUnitTree, isLoading, isSuccess, error } = useOrganisationUnitTreeContext();

  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    initTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationUnitTree]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const initTree = () => {
    const newExpandedNodes: string[] = [];
    if (organisationUnitTree) {
      const notExpanded = organisationUnitTree.map((x) => x.id).filter((id) => !newExpandedNodes.includes(id));
      newExpandedNodes.push(...notExpanded);
    }

    setExpandedNodes(newExpandedNodes);
  };

  const handleToggle = (event: React.SyntheticEvent | null, nodeIds: string[]) => {
    event?.persist();
    if (event?.target instanceof SVGElement || (event?.target as HTMLElement).className === 'MuiTreeItem-iconContainer') {
      setExpandedNodes(nodeIds);
    }
  };

  const handleSelect = (event: SyntheticEvent<Element, Event>, nodeId: string) => {
    const selectedNode = nodesByNodeId.get(nodeId)!;
    if (hasSelectedParent(selectedNode) || !selectedNode.isSubscriptionChannel || isDisabled(selectedNode)) {
      return;
    }

    event.persist();
    if (event.target instanceof HTMLElement && event.target.className !== 'MuiTreeItem-iconContainer') {
      if (nodesByNodeId.has(nodeId)) {
        onSelect(nodeId);
      }
    }
  };

  function completeParentUpstream(node: OrganisationTreeNode): OrganisationTreeNode {
    const nodeCopy = { ...node };
    if (!nodeCopy.parentId) {
      return nodeCopy;
    }

    const parentNode = nodesByNodeId.get(nodeCopy.parentId);
    if (!parentNode) {
      return nodeCopy;
    }

    const parent = completeParentUpstream(parentNode);
    nodeCopy.parentOrganisationUnit = parent;
    return nodeCopy;
  }

  function onSelect(nodeId: string): void {
    const selectedNode = completeParentUpstream(nodesByNodeId.get(nodeId)!);
    props.onSelectOrganisationUnit(selectedNode);
  }

  function hasSelectedParent(node: OrganisationTreeNode): boolean {
    if (!node.parentId) {
      return false;
    }

    return props.selectedOrganisationUnitIds.includes(node.parentId) || hasSelectedParent(nodesByNodeId.get(node.parentId)!);
  }

  function isDisabled(node: OrganisationTreeNode): boolean {
    if (props.disabledChildrenOfOrganisationUnitIds.includes(node.id)) {
      return false;
    }

    if (!node.parentId) {
      return false;
    }

    return props.disabledChildrenOfOrganisationUnitIds.includes(node.parentId) || isDisabled(nodesByNodeId.get(node.parentId)!);
  }

  const renderCheckbox = (node: OrganisationTreeNode) => (
    <Checkbox
      sx={{
        padding: '6px',
        margin: 0,
        marginLeft: node.childOrganisationUnits.some((x) => x) ? '0px' : '32px',
        '&:hover': {
          backgroundColor: alpha('#241A0029', 0.16),
        },
      }}
      size="small"
      checked={props.selectedOrganisationUnitIds?.some((o) => o === node.id) || hasSelectedParent(node)}
    />
  );

  function renderChildNodes(node: OrganisationTreeNode): ReactElement[] | undefined {
    nodesByNodeId.set(node.id, node);

    return node.childOrganisationUnits?.map((child) => (
      <OrganisationUnitTreeItem
        key={child.id}
        itemId={child.id}
        labelText={child.name}
        disableSelection={hasSelectedParent(child) || !child.isSubscriptionChannel || isDisabled(child)}
        highlightColor={'transparent'}
        highlightedHoverColor={alpha(Constants.Colors.secondaryContainer, 0.16)}
        startingIcons={renderCheckbox(child)}
        isRoot={false}
        data-testid={`multiSelectAlternativeDiversionTreeItem-${child.id}`}
      >
        {renderChildNodes(child)}
      </OrganisationUnitTreeItem>
    ));
  }

  return (
    <>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        isSuccess &&
        organisationUnitTree && (
          <div className="tree-view-container" data-testid="multiSelectAlternativeDiversionTree">
            <SimpleTreeView
              aria-label="rich object"
              className="tree-view"
              onExpandedItemsChange={handleToggle}
              onItemClick={handleSelect}
              expandedItems={expandedNodes}
            >
              {organisationUnitTree.map((rootItem) => (
                <OrganisationUnitTreeItem
                  key={rootItem.id}
                  itemId={rootItem.id}
                  labelText={rootItem.name}
                  disableSelection={hasSelectedParent(rootItem) || !rootItem.isSubscriptionChannel || isDisabled(rootItem)}
                  highlightColor={'transparent'}
                  highlightedHoverColor={alpha(Constants.Colors.secondaryContainer, 0.16)}
                  startingIcons={renderCheckbox(rootItem)}
                  isRoot={false}
                  data-testid={`multiSelectAlternativeDiversionTreeItem-${rootItem.id}`}
                >
                  {renderChildNodes(rootItem)}
                </OrganisationUnitTreeItem>
              ))}
            </SimpleTreeView>
          </div>
        )
      )}
    </>
  );
}
